import React, { useState, useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Recipe, RecipeCacheState, fetchRecipe } from '../utils/recipeUtils';
import { MatchedMeal, Meal } from '../utils/dataTypes'; // Import from the new dataTypes file
import { auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { trackButtonClick, trackNutritionAnalysis, trackMealSaved } from '../utils/analytics';
import { getMealImageUrl } from '../utils/imageUtils';
import '../styles/MealsByIngredients.css';
import { validateIngredient, filterIngredients } from '../utils/ingredientValidation';

// Function to clean recipe titles by removing specific words
const cleanRecipeTitle = (title: string): string => {
  return title
    .replace(/\bsimple\s+/i, '')
    .replace(/\bclassic\s+/i, '')
    .trim();
};

// List of common meat types for filtering
const MEAT_TYPES = [
  'beef', 'steak', 'brisket', 'veal', 
  'chicken', 'turkey', 'duck', 'goose', 'quail',
  'pork', 'ham', 'bacon', 'sausage', 'chorizo', 
  'lamb', 'mutton', 
  'fish', 'salmon', 'tuna', 'cod', 'tilapia', 'halibut', 'trout',
  'shrimp', 'crab', 'lobster', 'oyster', 'mussel', 'clam', 'scallop',
  'venison', 'rabbit', 'bison', 'buffalo'
];

// Function to identify meat types in ingredients
const identifyMeatTypes = (ingredients: string[]): string[] => {
  const lowercaseIngredients = ingredients.map(ing => ing.toLowerCase());
  return MEAT_TYPES.filter(meat => 
    lowercaseIngredients.some(ing => 
      ing.includes(meat) || 
      // Handle plural forms
      ing.includes(meat + 's')
    )
  );
};

// Function to filter meals based on meat type compatibility
const filterMealsByMeatType = (meals: MatchedMeal[], userIngredients: string[]): MatchedMeal[] => {
  // Identify meat types in user ingredients
  const userMeatTypes = identifyMeatTypes(userIngredients);
  
  // If no specific meat type is being searched for, return all meals
  if (userMeatTypes.length === 0) {
    return meals;
  }
  
  return meals.filter(meal => {
    // If meal has no ingredients, keep it
    if (!meal.ingredients || meal.ingredients.length === 0) {
      return true;
    }
    
    const mealMeatTypes = identifyMeatTypes(meal.ingredients);
    
    // If meal has no meat, keep it
    if (mealMeatTypes.length === 0) {
      return true;
    }
    
    // Check if any of the user's meat types are in the meal's meat types
    const hasCompatibleMeat = userMeatTypes.some(userMeat => 
      mealMeatTypes.includes(userMeat)
    );
    
    // If meal has compatible meat OR no meat types from user search, keep it
    return hasCompatibleMeat || userMeatTypes.length === 0;
  });
};

// Temporary interface to bridge the old and new types during transition
interface LegacyMeal {
  id?: string;
  mealId?: string;
  title?: string;
  name?: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  ingredients: string[];
  timeToPrep?: string;
}

// Recipe modal component
interface RecipeModalProps {
  isOpen: boolean;
  recipe: Recipe | null;
  loading: boolean;
  onClose: () => void;
  mealId?: string;
  onSaveRecipe?: () => void;
}

const RecipeModal: React.FC<RecipeModalProps> = ({ isOpen, recipe, loading, onClose, mealId, onSaveRecipe }) => {
  if (!isOpen) return null;

  return (
    <div className="recipe-modal-backdrop" onClick={onClose}>
      <div className="recipe-modal" onClick={(e) => e.stopPropagation()}>
        <div className="recipe-modal-header">
          <h2>{recipe?.title || 'Loading Recipe...'}</h2>
          <button className="close-modal" onClick={onClose}>×</button>
        </div>
        <div className="recipe-modal-content">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading recipe details...</p>
            </div>
          ) : recipe ? (
            <>
              {mealId && (
                <div className="recipe-modal-image">
                  <img 
                    src={getMealImageUrl(mealId)} 
                    alt={recipe.title} 
                    className="recipe-detail-image"
                  />
                </div>
              )}
              
              <p className="recipe-description">{recipe.description}</p>
              
              <div className="recipe-meta">
                <div className="recipe-meta-item">
                  <span className="meta-label">Prep Time:</span> {recipe.prepTime}
                </div>
                <div className="recipe-meta-item">
                  <span className="meta-label">Cook Time:</span> {recipe.cookTime}
                </div>
                <div className="recipe-meta-item">
                  <span className="meta-label">Total Time:</span> {recipe.totalTime}
                </div>
                <div className="recipe-meta-item">
                  <span className="meta-label">Servings:</span> {recipe.servings}
                </div>
              </div>
              
              <div className="recipe-section">
                <h3>Ingredients</h3>
                <ul className="recipe-ingredients">
                  {recipe.ingredients.map((ingredient, index) => (
                    <li key={index}>{ingredient}</li>
                  ))}
                </ul>
              </div>
              
              <div className="recipe-section">
                <h3>Instructions</h3>
                <ol className="recipe-instructions">
                  {recipe.instructions.map((instruction, index) => (
                    <li key={index}>{instruction}</li>
                  ))}
                </ol>
              </div>
              
              <div className="recipe-section">
                <h3>Nutrition Information</h3>
                <div className="nutrition-info">
                  <div className="nutrition-item">
                    <div className="nutrition-value">{recipe.nutritionInfo.calories}</div>
                    <div className="nutrition-label">Calories</div>
                  </div>
                  <div className="nutrition-item">
                    <div className="nutrition-value">{recipe.nutritionInfo.protein}g</div>
                    <div className="nutrition-label">Protein</div>
                  </div>
                  <div className="nutrition-item">
                    <div className="nutrition-value">{recipe.nutritionInfo.carbs}g</div>
                    <div className="nutrition-label">Carbs</div>
                  </div>
                  <div className="nutrition-item">
                    <div className="nutrition-value">{recipe.nutritionInfo.fat}g</div>
                    <div className="nutrition-label">Fat</div>
                  </div>
                </div>
              </div>
              
              {recipe.tips.length > 0 && (
                <div className="recipe-section">
                  <h3>Tips</h3>
                  <ul className="recipe-tips">
                    {recipe.tips.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="error-message">
              Could not load recipe. Please try again later.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MealsByIngredients: React.FC = () => {
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [matchedMeals, setMatchedMeals] = useState<MatchedMeal[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [selectedMeal, setSelectedMeal] = useState<Meal | null>(null);
  const [currentRecipe, setCurrentRecipe] = useState<Recipe | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [recipeLoading, setRecipeLoading] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [savedRecipes, setSavedRecipes] = useState<RecipeCacheState>({});
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [savingMeal, setSavingMeal] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check for authenticated user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    
    return () => unsubscribe();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (errorMessage) setErrorMessage('');
  };

  const addIngredient = () => {
    if (!inputValue.trim()) {
      setErrorMessage('Please enter an ingredient');
      return;
    }

    if (ingredients.includes(inputValue.trim().toLowerCase())) {
      setErrorMessage('This ingredient is already added');
      return;
    }

    // Validate ingredient against banned substances
    const validation = validateIngredient(inputValue.trim());
    if (!validation.valid) {
      setErrorMessage(`Invalid ingredient: ${validation.reason}`);
      return;
    }

    setIngredients([...ingredients, inputValue.trim().toLowerCase()]);
    setInputValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      addIngredient();
    }
  };

  const removeIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const findMeals = async () => {
    if (ingredients.length === 0) {
      setErrorMessage('Please add at least one ingredient');
      return;
    }

    // Perform a final validation check on all ingredients before sending to the API
    const { valid, invalid } = filterIngredients(ingredients);
    
    if (invalid.length > 0) {
      // If any invalid ingredients were found, show an error and remove them
      setErrorMessage(`Some ingredients contain banned substances and have been removed: ${invalid.map(item => item.ingredient).join(', ')}`);
      setIngredients(valid);
      return;
    }

    setIsLoading(true);
    setFetchError(null);
    
    // Track search action
    trackButtonClick('find_meals_by_ingredients', 'ingredients_page');
    
    try {
      // Call the API to search meals by ingredients - now using validated ingredients
      const apiEndpoint = `${process.env.REACT_APP_API_GATEWAY_URL}/prod/searchMealsByIngredients`;
      
      const response = await fetch(`${apiEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ingredients: valid // Use validated ingredients
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch meals: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && data.matchedMeals) {
        // Process the matched meals to ensure consistent structure
        const processedMeals = data.matchedMeals.map((meal: any) => {
          // Handle nested nutritionInfo structure if present
          if (meal.nutritionInfo) {
            return {
              ...meal,
              title: meal.title ? cleanRecipeTitle(meal.title) : meal.title,
              calories: meal.nutritionInfo.calories,
              protein: meal.nutritionInfo.protein,
              carbs: meal.nutritionInfo.carbs,
              fat: meal.nutritionInfo.fat
            };
          }
          return {
            ...meal,
            title: meal.title ? cleanRecipeTitle(meal.title) : meal.title
          };
        });
        
        // Sort meals by ingredient usage first, then by recipe completeness
        const sortedMeals = processedMeals.sort((a: MatchedMeal, b: MatchedMeal) => {
          // Get ingredient usage percentage
          const usageA = a.userIngredientsUsedPercentage || Math.round(a.matchPercentage * 0.3);
          const usageB = b.userIngredientsUsedPercentage || Math.round(b.matchPercentage * 0.3);
          
          // First sort by ingredient usage
          if (usageB !== usageA) {
            return usageB - usageA; // Sort in descending order
          }
          
          // If ingredient usage is the same, sort by recipe completeness
          const coverageA = a.mealCoveragePercentage || Math.round(a.matchPercentage * 0.7);
          const coverageB = b.mealCoveragePercentage || Math.round(b.matchPercentage * 0.7);
          return coverageB - coverageA; // Sort in descending order
        });
        
        // Filter meals by meat type compatibility
        const filteredByMeatType = filterMealsByMeatType(sortedMeals, ingredients);
        
        setMatchedMeals(filteredByMeatType);
        setShowResults(true);
        setIsLoading(false);
      } else if (data.matchedMeals && data.matchedMeals.length === 0) {
        // No meals found with these ingredients
        setMatchedMeals([]);
        setShowResults(true);
      } else {
        // Invalid response format
        console.warn('Invalid API response format');
        setMatchedMeals([]);
        setShowResults(true);
        setFetchError('Received invalid response format from the server. Please try again later.');
      }
    } catch (error) {
      console.error('Error searching for meals:', error);
      setFetchError('Failed to search for meals. Please try again later.');
      setMatchedMeals([]);
      setShowResults(true);
      setIsLoading(false);
    }
  };

  const viewMealRecipe = async (meal: MatchedMeal) => {
    // Convert MatchedMeal to LegacyMeal format for the existing fetchRecipe function
    const legacyMeal: LegacyMeal = {
      mealId: meal.mealId,
      title: meal.title ? cleanRecipeTitle(meal.title) : meal.title,
      calories: meal.calories,
      protein: meal.protein,
      carbs: meal.carbs,
      fat: meal.fat,
      ingredients: meal.ingredients,
      timeToPrep: meal.timeToPrep
    };

    setSelectedMeal(meal);
    setIsModalOpen(true);
    setRecipeLoading(true);
    
    // Track recipe view
    trackButtonClick('view_recipe', 'ingredients_search');
    trackNutritionAnalysis(meal.title || 'Unknown Meal');
    
    // The fetchRecipe function will:
    // 1. Check if the recipe is already in the cache
    // 2. If not in cache, try to fetch from the database if user is logged in
    // 3. If not in database or user not logged in, generate a temporary recipe
    await fetchRecipe(
      legacyMeal as any, // Type cast to work with existing fetchRecipe
      savedRecipes,
      setSavedRecipes,
      setCurrentRecipe,
      setRecipeLoading
    );
  };

  const closeRecipeModal = () => {
    setIsModalOpen(false);
    setSelectedMeal(null);
    setSaveSuccess(null);
  };

  // Save meal to user's profile
  const saveMeal = async (meal: MatchedMeal) => {
    // Clear previous states
    setSaveSuccess(null);
    setSaveError(null);
    
    let userId = currentUser?.uid;
    
    // If no user is logged in, redirect to login page
    if (!userId) {
      trackButtonClick('save_meal_login_redirect', 'ingredients_search');
      navigate('/login', { state: { from: '/recipes-by-ingredients' } });
      return;
    }

    try {
      // Set the meal being saved
      if (meal.mealId) {
        setSavingMeal(meal.mealId);
      }
      
      // Store the original meal ID before saving
      const originalMealId = meal.mealId;
      
      const apiEndpoint = `${process.env.REACT_APP_API_GATEWAY_URL}/prod/savemeal`;
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          meal: {
            title: meal.title,
            calories: meal.calories,
            protein: meal.protein,
            carbs: meal.carbs,
            fat: meal.fat,
            ingredients: meal.ingredients || [],
            timeToPrep: meal.timeToPrep || '15-20 minutes',
            originalMealId: originalMealId
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save meal');
      }

      const data = await response.json();
      
      // If we got a new mealId back, create a mapping to the original mealId
      if (data.mealId && originalMealId) {
        // Save mapping logic for original meal ID to new meal ID
        const saveMealIdMapping = (origId: string, newId: string) => {
          try {
            const mappings = localStorage.getItem('aiNutrition_mealIdMappings');
            let idMap: Record<string, string> = {};
            
            if (mappings) {
              idMap = JSON.parse(mappings) as Record<string, string>;
            }
            
            idMap[newId] = origId;
            localStorage.setItem('aiNutrition_mealIdMappings', JSON.stringify(idMap));
          } catch (error) {
            console.error('Error saving meal ID mapping:', error);
          }
        };
        
        saveMealIdMapping(originalMealId, data.mealId);
      }
      
      // Track meal saved event
      trackMealSaved(meal.title || 'Unknown Meal');
      
      // Set save success for this meal
      if (meal.mealId) {
        setSaveSuccess(meal.mealId);
      }
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving meal:', error);
      setSaveError('Failed to save meal');
      setTimeout(() => setSaveError(null), 3000);
    } finally {
      setSavingMeal(null);
    }
  };

  // Add function to save recipe for the modal
  const saveRecipe = () => {
    if (!selectedMeal) return;
    // Cast the selected meal to MatchedMeal type since it should already have all the required properties
    saveMeal(selectedMeal as MatchedMeal);
  };

  return (
    <div className="meals-by-ingredients-container">
      <Helmet>
        <title>Find Meals by Ingredients | aiNutrition</title>
        <meta name="description" content="Find meals based on ingredients you have available in your kitchen." />
      </Helmet>

      <header className="page-header">
        <h1>Find Meals by Ingredients</h1>
        <p className="section-description">
          Enter the ingredients you have available, and we'll suggest meals you can make.
        </p>
      </header>

      <section className="ingredients-input-section">
        <div className="input-container">
          <input
            type="text"
            className="ingredient-input"
            placeholder="Enter an ingredient (e.g., eggs, chicken, pasta)"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
          />
          <button className="btn primary-btn add-ingredient-btn" onClick={addIngredient}>
            Add
          </button>
        </div>
        
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        
        {fetchError && <div className="fetch-error-message">{fetchError}</div>}
        
        <div className="ingredients-list">
          {ingredients.length > 0 ? (
            <>
              <h3>Your Ingredients:</h3>
              <div className="ingredients-chips">
                {ingredients.map((ingredient, index) => (
                  <div key={index} className="ingredient-chip">
                    {ingredient}
                    <button
                      className="remove-ingredient"
                      onClick={() => removeIngredient(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-ingredients">
              No ingredients added yet. Start by adding ingredients you have.
            </div>
          )}
        </div>
        
        <button
          className="btn primary-btn find-recipes-btn"
          onClick={findMeals}
          disabled={ingredients.length === 0}
        >
          Find Meals
        </button>
      </section>
      
      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Finding meals with your ingredients...</p>
        </div>
      ) : showResults && (
        <section className="recipes-results-section">
          <h2>Meals with Your Ingredients</h2>
          
          {matchedMeals.length > 0 ? (
            <div className="recipes-grid">
              {matchedMeals.map((meal, index) => (
                <div key={index} className="recipe-card">
                  <div className="recipe-image">
                    {meal.mealId ? (
                      <img 
                        src={getMealImageUrl(meal.mealId)} 
                        alt={meal.title} 
                        className="recipe-image-content"
                      />
                    ) : (
                      <div className="recipe-image-placeholder"></div>
                    )}
                  </div>
                  <div className="recipe-info">
                    <h3>{meal.title}</h3>
                    
                    <div className="nutrition-info">
                      <div className="nutrition-item">
                        <div className="nutrition-value">{meal.calories}</div>
                        <div className="nutrition-label">Cal</div>
                      </div>
                      <div className="nutrition-item">
                        <div className="nutrition-value">{meal.protein}g</div>
                        <div className="nutrition-label">Protein</div>
                      </div>
                      <div className="nutrition-item">
                        <div className="nutrition-value">{meal.carbs}g</div>
                        <div className="nutrition-label">Carbs</div>
                      </div>
                      <div className="nutrition-item">
                        <div className="nutrition-value">{meal.fat}g</div>
                        <div className="nutrition-label">Fat</div>
                      </div>
                    </div>
                    
                    <div className="prep-time">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span><strong>Prep Time:</strong> {meal.timeToPrep}</span>
                    </div>
                    
                    <div className="meal-match-status">
                      {(meal.mealCoveragePercentage || Math.round(meal.matchPercentage * 0.7)) >= 100 ? (
                        <div className="can-make">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                          </svg>
                          <span>You can make this with your ingredients!</span>
                        </div>
                      ) : (meal.mealCoveragePercentage || Math.round(meal.matchPercentage * 0.7)) >= 80 ? (
                        <div className="almost-make">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="8" y1="12" x2="16" y2="12"/>
                          </svg>
                          <span>You're missing a few ingredients</span>
                        </div>
                      ) : (
                        <div className="cannot-make">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="15" y1="9" x2="9" y2="15"/>
                            <line x1="9" y1="9" x2="15" y2="15"/>
                          </svg>
                          <span>You're missing a few ingredients</span>
                        </div>
                      )}
                      
                      <div className="info-tooltip">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="16" x2="12" y2="12"/>
                          <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                        <div className="tooltip-content">
                          <div className="tooltip-metric">
                            <span className="tooltip-label">Recipe completeness:</span>
                            <span className="tooltip-value">{meal.mealCoveragePercentage || Math.round(meal.matchPercentage * 0.7)}%</span>
                            <small>of this recipe can be made with your ingredients</small>
                          </div>
                          <div className="tooltip-metric">
                            <span className="tooltip-label">Ingredients usage:</span>
                            <span className="tooltip-value">{meal.userIngredientsUsedPercentage || Math.round(meal.matchPercentage * 0.3)}%</span>
                            <small>of your ingredients will be used</small>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {ingredients.length > 0 && meal.ingredients && meal.ingredients.length > 0 && (
                      <div className="ingredients-list">
                        <div className="ingredients-title">Ingredients:</div>
                        <div>
                          {meal.ingredients.map((ing, i) => (
                            <span 
                              key={i} 
                              className="ingredient-chip"
                              style={{
                                backgroundColor: ingredients.some(userIng => 
                                  ing.toLowerCase().includes(userIng.toLowerCase()) || 
                                  userIng.toLowerCase().includes(ing.toLowerCase())
                                ) ? '#e6f4ea' : '#f5f5f5'
                              }}
                            >
                              {ing}
                            </span>
                          ))}
                        </div>
                        <div className="missing-ingredients">
                          <small>
                            Missing: {meal.ingredients
                              .filter(ing => !ingredients.some(userIng => 
                                ing.toLowerCase().includes(userIng.toLowerCase()) || 
                                userIng.toLowerCase().includes(ing.toLowerCase())
                              ))
                              .join(', ')}
                          </small>
                        </div>
                      </div>
                    )}
                    
                    <div className="recipe-actions">
                      <button
                        className="btn primary-btn view-recipe-btn"
                        onClick={() => viewMealRecipe(meal)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <line x1="16" y1="13" x2="8" y2="13"/>
                          <line x1="16" y1="17" x2="8" y2="17"/>
                          <polyline points="10 9 9 9 8 9"/>
                        </svg>
                        View Recipe
                      </button>
                      <button 
                        className={`btn primary-btn save-meal-btn ${savingMeal === meal.mealId ? 'saving' : ''} ${saveSuccess === meal.mealId ? 'success' : ''}`}
                        onClick={() => saveMeal(meal)}
                        disabled={savingMeal === meal.mealId}
                      >
                        {savingMeal === meal.mealId ? (
                          <span className="saving-indicator">Saving...</span>
                        ) : saveSuccess === meal.mealId ? (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                              <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                            Saved
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="7 10 12 15 17 10"/>
                              <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            Save
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              No meals found with these ingredients. Try adding different ingredients or removing some.
            </div>
          )}
        </section>
      )}
      
      <RecipeModal
        isOpen={isModalOpen}
        recipe={currentRecipe}
        loading={recipeLoading}
        onClose={closeRecipeModal}
        mealId={selectedMeal?.mealId}
        onSaveRecipe={saveRecipe}
      />
      
      {saveError && (
        <div className="save-error-message">
          {saveError}
        </div>
      )}
    </div>
  );
};

export default MealsByIngredients;

